import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Mail, MessageSquare, RefreshCw, Sparkles, ShieldAlert, Flame,
  CheckSquare, Calendar, Briefcase, ArrowRight, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { ScoreChip } from "../components/ScoreChip";
import { PhoneLink, EmailLink } from "../components/ContactLinks";
import { toast } from "sonner";

/**
 * Run My Day — the single command center.
 *
 * Perf strategy (see PRD "Run My Day speed"):
 *   • Two cheap, indexed calls fire on mount in parallel:
 *       GET /dashboard              — stats, tasks, appointments
 *       GET /ai/crm/who-to-call/cached — last cached LLM briefing
 *     Together they paint the page in <500ms even on a cold cache.
 *   • If the cached briefing is missing or stale (>4h), we kick off the
 *     slow POST /ai/crm/who-to-call in the BACKGROUND. The UI shows the
 *     cached briefing (if any) plus a quiet "Refreshing…" pill; the
 *     recommendations swap in whenever the LLM returns.
 *   • Manual Refresh always hits the slow POST — that's the escape hatch
 *     when the agent knows something changed and wants fresh drafts now.
 */

function ChannelIcon({ channel }) {
  if (channel === "call") return <Phone className="h-3.5 w-3.5" />;
  if (channel === "text") return <MessageSquare className="h-3.5 w-3.5" />;
  if (channel === "email") return <Mail className="h-3.5 w-3.5" />;
  return <ShieldAlert className="h-3.5 w-3.5" />;
}

function Row({ rec, rank }) {
  const noChannel = !rec.draft_channel;
  return (
    <div className="grid grid-cols-[auto_1fr] gap-4 sm:gap-6 py-6 border-b border-slate-200" data-testid={`rec-row-${rank}`}>
      <div className="pt-1">
        <div className="font-heading text-3xl sm:text-4xl font-bold text-slate-300 tabular-nums leading-none">{String(rank).padStart(2, "0")}</div>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Link to={`/leads/${rec.lead_id}`} className="font-heading text-lg font-semibold tracking-tight text-slate-900 hover:underline break-words" data-testid={`rec-name-${rank}`}>{rec.lead_name}</Link>
          <ScoreChip temperature={rec.temperature} score={rec.score} />
        </div>
        {(rec.phone || rec.email) ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid={`rec-contact-${rank}`}>
            {rec.phone && (
              <PhoneLink
                phone={rec.phone}
                testId={`rec-phone-${rank}`}
                smsBody={rec.draft_channel === "text" ? rec.draft_message : undefined}
              />
            )}
            {rec.email && (
              <EmailLink
                email={rec.email}
                subject={rec.draft_channel === "email" ? undefined : null}
                testId={`rec-email-${rank}`}
              />
            )}
          </div>
        ) : (
          <div className="mt-1.5 text-xs text-slate-500 uppercase tracking-wider">no channel</div>
        )}
        <p className="mt-2 text-[15px] text-slate-800 leading-relaxed" data-testid={`rec-headline-${rank}`}>{rec.headline}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-1 text-white text-xs font-medium">
            <ChannelIcon channel={rec.draft_channel} />
            {rec.draft_channel || "no consent"}
          </span>
          <span className="text-slate-700">{rec.next_action}</span>
        </div>
        {rec.draft_message && (
          <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 max-w-2xl">
            <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 mb-2">Draft {rec.draft_channel === "call" ? "call opener" : "message"} · pending your approval</div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid={`rec-draft-${rank}`}>{rec.draft_message}</p>
          </div>
        )}
        {noChannel && (
          <div className="mt-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 max-w-2xl text-sm text-amber-900">
            No consent on file — automated outreach is blocked. A manual phone call is allowed.
          </div>
        )}
      </div>
    </div>
  );
}

// Skeleton row shown when we have absolutely no data yet (cold cache).
function SkeletonRow({ rank }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-4 sm:gap-6 py-6 border-b border-slate-200" data-testid={`rec-skeleton-${rank}`}>
      <div className="pt-1">
        <div className="font-heading text-3xl sm:text-4xl font-bold text-slate-200 tabular-nums leading-none">{String(rank).padStart(2, "0")}</div>
      </div>
      <div className="min-w-0 space-y-3">
        <div className="h-5 w-48 rounded bg-slate-100 animate-pulse" />
        <div className="h-4 w-3/4 rounded bg-slate-100 animate-pulse" />
        <div className="h-4 w-1/2 rounded bg-slate-100 animate-pulse" />
      </div>
    </div>
  );
}

export default function RunMyDay() {
  const [dash, setDash] = useState(null);           // stats + tasks
  const [briefing, setBriefing] = useState(null);   // AI recommendations (cached or fresh)
  const [cachedAt, setCachedAt] = useState(null);   // ISO string
  const [refreshing, setRefreshing] = useState(false); // background LLM call in flight
  const [error, setError] = useState(null);
  const bgRefreshFired = useRef(false);

  // Kick a slow LLM refresh in the background. Never blocks paint.
  const refreshBriefing = useCallback(async ({ manual = false } = {}) => {
    setRefreshing(true);
    setError(null);
    try {
      const { data } = await api.post("/ai/crm/who-to-call?limit=5");
      setBriefing(data);
      setCachedAt(new Date().toISOString());
      if (manual) toast.success("Briefing refreshed");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't refresh briefing";
      if (manual) toast.error(detail);
      // On background failure, leave the existing cached briefing in place
      // — a stale briefing is much better than an error state.
      if (!briefing) setError(detail);
    } finally {
      setRefreshing(false);
    }
  }, [briefing]);

  // Initial paint: fire two cheap parallel reads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [dashRes, cachedRes] = await Promise.all([
        api.get("/dashboard?minimal=1").catch(() => ({ data: null })),
        api.get("/ai/crm/who-to-call/cached").catch(() => ({ data: { cached: null, stale: true } })),
      ]);
      if (cancelled) return;
      setDash(dashRes.data);
      setBriefing(cachedRes.data?.cached || null);
      setCachedAt(cachedRes.data?.cached_at || null);

      // Background refresh only if we have no cache OR the cache is stale.
      // Guard against StrictMode's double-invoke firing two LLM calls.
      if (!bgRefreshFired.current && (cachedRes.data?.stale || !cachedRes.data?.cached)) {
        bgRefreshFired.current = true;
        refreshBriefing({ manual: false });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recs = briefing?.recommendations || [];
  const showSkeleton = !briefing && refreshing;
  const cachedLabel = cachedAt
    ? new Date(cachedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <div>
      {/* Compact stats row — folded in from the old /dashboard page so
          Run My Day is the single command center. Hot-lead count is derived
          from the cached briefing (fastest path) with a fallback to the
          dashboard payload for the moment before the briefing loads. */}
      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="dashboard-stats">
          {[
            {
              label: "Hot leads",
              value: (recs.filter(r => r.temperature === "hot").length) || (dash.hot_leads || []).filter(l => l.temperature === "hot").length,
              icon: Flame,
              to: "/leads?filter=hot",
            },
            { label: "Active transactions", value: dash.active_transactions_count, icon: Briefcase, to: "/transactions" },
            { label: "Open tasks", value: dash.open_tasks_count, icon: CheckSquare, to: "/tasks" },
            { label: "Appts today", value: (dash.appointments_today || []).length, icon: Calendar, to: "/tasks" },
          ].map(s => (
            <Link
              key={s.label}
              to={s.to}
              className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 hover:shadow-sm transition-all"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="h-8 w-8 shrink-0 rounded-md bg-slate-50 flex items-center justify-center">
                <s.icon className="h-4 w-4 text-slate-500 group-hover:text-slate-900" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 truncate">{s.label}</div>
                <div className="font-heading text-xl font-bold tabular-nums leading-tight text-slate-900">{s.value}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 ml-auto" />
            </Link>
          ))}
        </div>
      )}

      <div className="flex items-start justify-between gap-3 sm:gap-6 mb-8 flex-wrap sm:flex-nowrap">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "var(--primary-hex)" }} />
            Run My Day · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">Who to call today.</h1>
          {briefing?.summary && (
            <p className="mt-3 text-[15px] text-slate-700 leading-relaxed max-w-2xl" data-testid="briefing-summary">{briefing.summary}</p>
          )}
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            {briefing?.provider && <span>Language by {briefing.provider}</span>}
            {cachedLabel && !refreshing && briefing && <span data-testid="briefing-cached-at">· Updated {cachedLabel}</span>}
            {refreshing && briefing && (
              <span className="inline-flex items-center gap-1 text-slate-500" data-testid="briefing-refreshing">
                <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => refreshBriefing({ manual: true })}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 shrink-0 disabled:opacity-50"
          data-testid="refresh-briefing-btn"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && !briefing && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="briefing-error">{error}</div>
      )}

      {/* Cold-cache skeleton — first-ever load, LLM still working. */}
      {showSkeleton && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="briefing-skeleton">
          <div className="px-4 sm:px-6">
            {[1, 2, 3].map(i => <SkeletonRow key={i} rank={i} />)}
          </div>
        </div>
      )}

      {briefing && recs.length === 0 && !refreshing && (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center" data-testid="empty-state">
          <p className="font-heading text-lg font-semibold text-slate-900">Nothing to call yet.</p>
          <p className="mt-1 text-sm text-slate-500">Add a lead to get your first ranked recommendation.</p>
          <Link to="/leads/new" className="mt-4 inline-block rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }}>Add a lead</Link>
        </div>
      )}

      {recs.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="briefing-list">
          <div className="px-4 sm:px-6">
            {recs.map((r, i) => (
              <Row key={r.lead_id} rec={r} rank={i + 1} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
